import type { ReportKpi, ReportTemplate, ReportPeriod } from '@/shared/components'
import { TRAINING_KPIS, STUDENTS } from './training'

export interface ReportsModuleData {
  moduleKey: string
  moduleTitle: string
  modulePath: string
  color: string
  kpis: Record<ReportPeriod, ReportKpi[]>
  trendChart: {
    title: string
    categories: string[]
    series: { name: string; data: number[]; color?: string }[]
  }
  distributionChart: {
    title: string
    data: { name: string; value: number; color?: string }[]
  }
  templates: ReportTemplate[]
}

const TAC_CHIEN: ReportsModuleData = {
  moduleKey: 'tac-chien',
  moduleTitle: 'Tác chiến — Huấn luyện — An toàn bay',
  modulePath: '/tac-chien',
  color: '#ef4444',
  kpis: {
    week: [
      { label: 'Ca trực ban', value: 28, color: '#1e6091' },
      { label: 'Sự cố ATB', value: 0, color: '#10b981' },
      { label: 'Lượt huấn luyện', value: 142, color: '#f59e0b', trend: { value: 8, positive: true } },
      { label: 'Trinh sát ghi nhận', value: 17, color: '#8b5cf6' },
    ],
    month: [
      { label: 'Ca trực ban', value: 124, color: '#1e6091' },
      { label: 'Sự cố ATB', value: 3, color: '#ef4444', trend: { value: 25, positive: true } },
      { label: 'Lượt huấn luyện', value: 612, color: '#f59e0b', trend: { value: 5, positive: true } },
      { label: 'Trinh sát ghi nhận', value: 78, color: '#8b5cf6' },
    ],
    quarter: [
      { label: 'Ca trực ban', value: 372, color: '#1e6091' },
      { label: 'Sự cố ATB', value: 8, color: '#ef4444' },
      { label: 'Lượt huấn luyện', value: 1856, color: '#f59e0b' },
      { label: 'Trinh sát ghi nhận', value: 248, color: '#8b5cf6' },
    ],
    year: [
      { label: 'Ca trực ban', value: 1488, color: '#1e6091' },
      { label: 'Sự cố ATB', value: 31, color: '#ef4444' },
      { label: 'Lượt huấn luyện', value: 7480, color: '#f59e0b' },
      { label: 'Trinh sát ghi nhận', value: 1024, color: '#8b5cf6' },
    ],
  },
  trendChart: {
    title: 'Lượt huấn luyện theo ngày (30 ngày)',
    categories: Array.from({ length: 30 }, (_, i) => `${i + 1}`),
    series: [
      { name: 'Huấn luyện bay', data: Array.from({ length: 30 }, () => Math.round(10 + Math.random() * 25)), color: '#1e6091' },
      { name: 'Huấn luyện dù', data: Array.from({ length: 30 }, () => Math.round(2 + Math.random() * 8)), color: '#06b6d4' },
    ],
  },
  distributionChart: {
    title: 'Sự cố ATB phân loại',
    data: [
      { name: 'Nhẹ', value: 18, color: '#10b981' },
      { name: 'Đáng kể', value: 9, color: '#f59e0b' },
      { name: 'Nghiêm trọng', value: 3, color: '#ef4444' },
      { name: 'Suýt sự cố', value: 12, color: '#06b6d4' },
    ],
  },
  templates: [
    { id: 'tc-1', code: 'TC-W-2026-W18', name: 'Báo cáo trực ban chiến đấu tuần 18', period: 'week', format: 'pdf', generatedAt: '2026-05-05T07:30:00', generatedBy: 'Trung uý Phạm Hoàng Sơn', size: '980 KB', status: 'ready' },
    { id: 'tc-2', code: 'TC-ATB-M-2026-04', name: 'Báo cáo an toàn bay tháng 4/2026', period: 'month', format: 'pdf', generatedAt: '2026-05-01T08:15:00', generatedBy: 'Thượng tá Trần Đức Tuấn', size: '2.4 MB', status: 'ready' },
    { id: 'tc-3', code: 'TC-HL-M-2026-04', name: 'Báo cáo huấn luyện bay/dù tháng 4', period: 'month', format: 'xlsx', generatedAt: '2026-05-01T09:00:00', generatedBy: 'Đại uý Lê Văn Tài', size: '1.8 MB', status: 'ready' },
    { id: 'tc-4', code: 'TC-TS-W-2026-W18', name: 'Báo cáo trinh sát tuần 18', period: 'week', format: 'docx', generatedAt: '2026-05-05T16:00:00', generatedBy: 'Đại uý Hoàng Văn Quang', size: '650 KB', status: 'ready' },
    { id: 'tc-5', code: 'TC-Q-2026-Q1', name: 'Báo cáo tổng hợp Quý 1/2026', period: 'quarter', format: 'pdf', generatedAt: '2026-04-05T10:00:00', generatedBy: 'Đại tá Nguyễn Văn Hùng', size: '6.2 MB', status: 'ready' },
  ],
}

const KE_HOACH_BAY: ReportsModuleData = {
  moduleKey: 'ke-hoach-bay',
  moduleTitle: 'Kế hoạch bay — Điều hành bay',
  modulePath: '/ke-hoach-bay',
  color: '#1e6091',
  kpis: {
    week: [
      { label: 'KHB thực hiện', value: 87, color: '#1e6091', trend: { value: 12, positive: true } },
      { label: 'Giờ bay tích luỹ', value: 412, suffix: 'giờ', color: '#10b981', trend: { value: 8, positive: true } },
      { label: 'Tỷ lệ phê duyệt', value: '94%', color: '#f59e0b' },
      { label: 'KHB huỷ', value: 4, color: '#ef4444', trend: { value: 20, positive: false } },
    ],
    month: [
      { label: 'KHB thực hiện', value: 342, color: '#1e6091', trend: { value: 5, positive: true } },
      { label: 'Giờ bay tích luỹ', value: 1842, suffix: 'giờ', color: '#10b981', trend: { value: 3, positive: true } },
      { label: 'Tỷ lệ phê duyệt', value: '96%', color: '#f59e0b' },
      { label: 'KHB huỷ', value: 12, color: '#ef4444', trend: { value: 15, positive: true } },
    ],
    quarter: [
      { label: 'KHB thực hiện', value: 1024, color: '#1e6091' },
      { label: 'Giờ bay tích luỹ', value: 5520, suffix: 'giờ', color: '#10b981' },
      { label: 'Tỷ lệ phê duyệt', value: '95%', color: '#f59e0b' },
      { label: 'KHB huỷ', value: 38, color: '#ef4444' },
    ],
    year: [
      { label: 'KHB thực hiện', value: 4128, color: '#1e6091' },
      { label: 'Giờ bay tích luỹ', value: 22480, suffix: 'giờ', color: '#10b981' },
      { label: 'Tỷ lệ phê duyệt', value: '95%', color: '#f59e0b' },
      { label: 'KHB huỷ', value: 156, color: '#ef4444' },
    ],
  },
  trendChart: {
    title: 'Số KHB & giờ bay theo ngày (30 ngày gần nhất)',
    categories: Array.from({ length: 30 }, (_, i) => `${i + 1}`),
    series: [
      { name: 'Số KHB', data: Array.from({ length: 30 }, () => Math.round(8 + Math.random() * 18)), color: '#1e6091' },
      { name: 'Giờ bay', data: Array.from({ length: 30 }, () => Math.round(40 + Math.random() * 80)), color: '#10b981' },
    ],
  },
  distributionChart: {
    title: 'KHB phân bổ theo loại nhiệm vụ',
    data: [
      { name: 'Huấn luyện bay', value: 142, color: '#1e6091' },
      { name: 'Trực sẵn sàng', value: 78, color: '#ef4444' },
      { name: 'Vận chuyển', value: 56, color: '#10b981' },
      { name: 'Trinh sát', value: 32, color: '#8b5cf6' },
      { name: 'Tuần tra', value: 24, color: '#f59e0b' },
      { name: 'Bay thử kỹ thuật', value: 10, color: '#6b7280' },
    ],
  },
  templates: [
    { id: 'khb-1', code: 'KHB-W-2026-W18', name: 'Báo cáo kế hoạch bay tuần 18/2026', period: 'week', format: 'pdf', generatedAt: '2026-05-05T08:00:00', generatedBy: 'Đại uý Nguyễn Hữu Nam', size: '1.2 MB', status: 'ready' },
    { id: 'khb-2', code: 'KHB-M-2026-04', name: 'Báo cáo kế hoạch bay tháng 4/2026', period: 'month', format: 'xlsx', generatedAt: '2026-05-01T09:30:00', generatedBy: 'Đại uý Nguyễn Hữu Nam', size: '3.4 MB', status: 'ready' },
    { id: 'khb-3', code: 'KHB-Q-2026-Q1', name: 'Báo cáo kế hoạch bay Quý 1/2026', period: 'quarter', format: 'pdf', generatedAt: '2026-04-05T10:15:00', generatedBy: 'Thượng tá Trần Đức Tuấn', size: '5.8 MB', status: 'ready' },
    { id: 'khb-4', code: 'KHB-PILOT-2026-W18', name: 'Báo cáo trình độ phi công tuần', period: 'week', format: 'xlsx', generatedAt: '2026-05-05T07:45:00', generatedBy: 'Đại uý Nguyễn Hữu Nam', size: '845 KB', status: 'ready' },
    { id: 'khb-5', code: 'KHB-AC-2026-04', name: 'Báo cáo tình trạng máy bay tháng 4', period: 'month', format: 'xlsx', generatedAt: '2026-05-01T08:00:00', generatedBy: 'Đại uý Lê Văn Đức', size: '2.1 MB', status: 'ready' },
    { id: 'khb-6', code: 'KHB-W-2026-W19', name: 'Báo cáo kế hoạch bay tuần 19/2026', period: 'week', format: 'pdf', generatedAt: '2026-05-12T08:00:00', generatedBy: 'Hệ thống tự động', size: '—', status: 'scheduled' },
  ],
}

const DAN_DUONG: ReportsModuleData = {
  moduleKey: 'dan-duong',
  moduleTitle: 'Dẫn đường bay',
  modulePath: '/dan-duong',
  color: '#8b5cf6',
  kpis: {
    week: [
      { label: 'Bài toán xử lý', value: 312, color: '#8b5cf6', trend: { value: 14, positive: true } },
      { label: 'Sai số trung bình', value: '±127', suffix: 'm', color: '#10b981' },
      { label: 'Phương án vũ khí', value: 48, color: '#f59e0b' },
      { label: 'Thời gian phản hồi', value: '0.8', suffix: 's', color: '#1e6091' },
    ],
    month: [
      { label: 'Bài toán xử lý', value: 1342, color: '#8b5cf6' },
      { label: 'Sai số trung bình', value: '±142', suffix: 'm', color: '#10b981' },
      { label: 'Phương án vũ khí', value: 218, color: '#f59e0b' },
      { label: 'Thời gian phản hồi', value: '0.9', suffix: 's', color: '#1e6091' },
    ],
    quarter: [
      { label: 'Bài toán xử lý', value: 4128, color: '#8b5cf6' },
      { label: 'Sai số trung bình', value: '±138', suffix: 'm', color: '#10b981' },
      { label: 'Phương án vũ khí', value: 612, color: '#f59e0b' },
      { label: 'Thời gian phản hồi', value: '0.85', suffix: 's', color: '#1e6091' },
    ],
    year: [
      { label: 'Bài toán xử lý', value: 16480, color: '#8b5cf6' },
      { label: 'Sai số trung bình', value: '±145', suffix: 'm', color: '#10b981' },
      { label: 'Phương án vũ khí', value: 2480, color: '#f59e0b' },
      { label: 'Thời gian phản hồi', value: '0.9', suffix: 's', color: '#1e6091' },
    ],
  },
  trendChart: {
    title: 'Số bài toán dẫn đường xử lý theo ngày',
    categories: Array.from({ length: 30 }, (_, i) => `${i + 1}`),
    series: [
      { name: 'Bài toán', data: Array.from({ length: 30 }, () => Math.round(30 + Math.random() * 60)), color: '#8b5cf6' },
    ],
  },
  distributionChart: {
    title: 'Phân bổ loại bài toán',
    data: [
      { name: 'Tính đường bay', value: 480, color: '#1e6091' },
      { name: 'Phương án vũ khí', value: 218, color: '#ef4444' },
      { name: 'Hiệu suất', value: 312, color: '#10b981' },
      { name: 'Hiệp đồng', value: 198, color: '#f59e0b' },
      { name: 'Mô phỏng', value: 134, color: '#06b6d4' },
    ],
  },
  templates: [
    { id: 'dd-1', code: 'DD-W-2026-W18', name: 'Báo cáo dẫn đường bay tuần 18', period: 'week', format: 'pdf', generatedAt: '2026-05-05T08:00:00', generatedBy: 'Thiếu tá Vũ Đình Long', size: '1.6 MB', status: 'ready' },
    { id: 'dd-2', code: 'DD-M-2026-04', name: 'Báo cáo dẫn đường tháng 4/2026', period: 'month', format: 'pdf', generatedAt: '2026-05-01T09:00:00', generatedBy: 'Thiếu tá Vũ Đình Long', size: '4.2 MB', status: 'ready' },
    { id: 'dd-3', code: 'DD-WP-M-2026-04', name: 'Phân tích phương án vũ khí tháng 4', period: 'month', format: 'xlsx', generatedAt: '2026-05-01T10:30:00', generatedBy: 'Đại uý Trần Văn Hoàng', size: '2.8 MB', status: 'ready' },
    { id: 'dd-4', code: 'DD-PERF-Q-2026-Q1', name: 'Đánh giá hiệu suất chiến đấu Quý 1', period: 'quarter', format: 'pdf', generatedAt: '2026-04-05T11:00:00', generatedBy: 'Đại tá Lê Quang Minh', size: '8.4 MB', status: 'ready' },
  ],
}

const VAN_TAI: ReportsModuleData = {
  moduleKey: 'van-tai',
  moduleTitle: 'Vận tải hàng không quân sự',
  modulePath: '/van-tai',
  color: '#10b981',
  kpis: {
    week: [
      { label: 'Chuyến vận tải', value: 42, color: '#10b981', trend: { value: 6, positive: true } },
      { label: 'Hành khách', value: 1248, color: '#1e6091' },
      { label: 'Hàng hoá', value: '38.2', suffix: 'tấn', color: '#f59e0b' },
      { label: 'Đúng giờ', value: '91%', color: '#06b6d4' },
    ],
    month: [
      { label: 'Chuyến vận tải', value: 184, color: '#10b981', trend: { value: 4, positive: true } },
      { label: 'Hành khách', value: 5240, color: '#1e6091' },
      { label: 'Hàng hoá', value: '162', suffix: 'tấn', color: '#f59e0b' },
      { label: 'Đúng giờ', value: '93%', color: '#06b6d4' },
    ],
    quarter: [
      { label: 'Chuyến vận tải', value: 548, color: '#10b981' },
      { label: 'Hành khách', value: 15820, color: '#1e6091' },
      { label: 'Hàng hoá', value: '486', suffix: 'tấn', color: '#f59e0b' },
      { label: 'Đúng giờ', value: '92%', color: '#06b6d4' },
    ],
    year: [
      { label: 'Chuyến vận tải', value: 2192, color: '#10b981' },
      { label: 'Hành khách', value: 63240, color: '#1e6091' },
      { label: 'Hàng hoá', value: '1944', suffix: 'tấn', color: '#f59e0b' },
      { label: 'Đúng giờ', value: '92%', color: '#06b6d4' },
    ],
  },
  trendChart: {
    title: 'Khối lượng vận tải theo ngày',
    categories: Array.from({ length: 30 }, (_, i) => `${i + 1}`),
    series: [
      { name: 'Hành khách', data: Array.from({ length: 30 }, () => Math.round(120 + Math.random() * 220)), color: '#1e6091' },
      { name: 'Hàng (tấn)', data: Array.from({ length: 30 }, () => Math.round(3 + Math.random() * 10)), color: '#f59e0b' },
    ],
  },
  distributionChart: {
    title: 'Tuyến vận tải',
    data: [
      { name: 'Bắc - Trung', value: 78, color: '#1e6091' },
      { name: 'Trung - Nam', value: 64, color: '#10b981' },
      { name: 'Bắc - Nam', value: 48, color: '#f59e0b' },
      { name: 'Đảo & xa bờ', value: 32, color: '#06b6d4' },
      { name: 'Nội vùng', value: 56, color: '#8b5cf6' },
    ],
  },
  templates: [
    { id: 'vt-1', code: 'VT-W-2026-W18', name: 'Báo cáo vận tải tuần 18', period: 'week', format: 'pdf', generatedAt: '2026-05-05T08:00:00', generatedBy: 'Đại uý Nguyễn Văn Tâm', size: '1.4 MB', status: 'ready' },
    { id: 'vt-2', code: 'VT-M-2026-04', name: 'Báo cáo vận tải tháng 4/2026', period: 'month', format: 'xlsx', generatedAt: '2026-05-01T08:30:00', generatedBy: 'Đại uý Nguyễn Văn Tâm', size: '2.6 MB', status: 'ready' },
    { id: 'vt-3', code: 'VT-PAX-M-2026-04', name: 'Báo cáo hành khách tháng 4', period: 'month', format: 'xlsx', generatedAt: '2026-05-01T09:00:00', generatedBy: 'Trung uý Lê Thị Mai', size: '1.8 MB', status: 'ready' },
    { id: 'vt-4', code: 'VT-CARGO-Q-2026-Q1', name: 'Báo cáo hàng hoá Quý 1', period: 'quarter', format: 'pdf', generatedAt: '2026-04-05T10:00:00', generatedBy: 'Đại uý Hoàng Văn Sơn', size: '4.8 MB', status: 'ready' },
  ],
}

const KHI_TUONG: ReportsModuleData = {
  moduleKey: 'khi-tuong',
  moduleTitle: 'Khí tượng',
  modulePath: '/khi-tuong',
  color: '#06b6d4',
  kpis: {
    week: [
      { label: 'Bản tin METAR', value: 1248, color: '#06b6d4' },
      { label: 'Bản tin TAF', value: 168, color: '#1e6091' },
      { label: 'Cảnh báo phát ra', value: 8, color: '#f59e0b' },
      { label: 'Trạm hoạt động', value: '26/27', color: '#10b981' },
    ],
    month: [
      { label: 'Bản tin METAR', value: 5424, color: '#06b6d4' },
      { label: 'Bản tin TAF', value: 720, color: '#1e6091' },
      { label: 'Cảnh báo phát ra', value: 32, color: '#f59e0b' },
      { label: 'Trạm hoạt động', value: '27/27', color: '#10b981' },
    ],
    quarter: [
      { label: 'Bản tin METAR', value: 16272, color: '#06b6d4' },
      { label: 'Bản tin TAF', value: 2160, color: '#1e6091' },
      { label: 'Cảnh báo phát ra', value: 124, color: '#f59e0b' },
      { label: 'Trạm hoạt động', value: '27/27', color: '#10b981' },
    ],
    year: [
      { label: 'Bản tin METAR', value: 65088, color: '#06b6d4' },
      { label: 'Bản tin TAF', value: 8640, color: '#1e6091' },
      { label: 'Cảnh báo phát ra', value: 486, color: '#f59e0b' },
      { label: 'Trạm hoạt động', value: '27/27', color: '#10b981' },
    ],
  },
  trendChart: {
    title: 'Bản tin & cảnh báo theo ngày (30 ngày)',
    categories: Array.from({ length: 30 }, (_, i) => `${i + 1}`),
    series: [
      { name: 'METAR', data: Array.from({ length: 30 }, () => Math.round(160 + Math.random() * 30)), color: '#06b6d4' },
      { name: 'Cảnh báo', data: Array.from({ length: 30 }, () => Math.round(Math.random() * 4)), color: '#f59e0b' },
    ],
  },
  distributionChart: {
    title: 'Cảnh báo theo loại',
    data: [
      { name: 'Tầm nhìn thấp', value: 14, color: '#06b6d4' },
      { name: 'Sương mù', value: 8, color: '#9ca3af' },
      { name: 'Dông sét', value: 6, color: '#f59e0b' },
      { name: 'Gió cắt', value: 3, color: '#ef4444' },
      { name: 'Bão', value: 1, color: '#8b5cf6' },
    ],
  },
  templates: [
    { id: 'kt-1', code: 'KT-W-2026-W18', name: 'Báo cáo khí tượng tuần 18', period: 'week', format: 'pdf', generatedAt: '2026-05-05T07:00:00', generatedBy: 'Thiếu tá Đoàn Văn Bình', size: '2.2 MB', status: 'ready' },
    { id: 'kt-2', code: 'KT-M-2026-04', name: 'Báo cáo khí tượng tháng 4/2026', period: 'month', format: 'pdf', generatedAt: '2026-05-01T07:30:00', generatedBy: 'Thiếu tá Đoàn Văn Bình', size: '5.6 MB', status: 'ready' },
    { id: 'kt-3', code: 'KT-WX-W-2026-W18', name: 'Tổng hợp cảnh báo thời tiết tuần', period: 'week', format: 'xlsx', generatedAt: '2026-05-05T08:00:00', generatedBy: 'Trung uý Phạm Thị Nga', size: '780 KB', status: 'ready' },
    { id: 'kt-4', code: 'KT-Q-2026-Q1', name: 'Báo cáo khí tượng Quý 1', period: 'quarter', format: 'pdf', generatedAt: '2026-04-05T08:00:00', generatedBy: 'Thượng tá Nguyễn Văn Hải', size: '12.4 MB', status: 'ready' },
  ],
}

const BAO_DONG: ReportsModuleData = {
  moduleKey: 'bao-dong',
  moduleTitle: 'Hệ thống Báo động phòng không',
  modulePath: '/bao-dong',
  color: '#f59e0b',
  kpis: {
    week: [
      { label: 'Mục tiêu phát hiện', value: 9384, color: '#1e6091', trend: { value: 8, positive: true } },
      { label: 'Cảnh báo đã xử lý', value: 248, color: '#f59e0b' },
      { label: 'Cảnh báo nghiêm trọng', value: 3, color: '#ef4444' },
      { label: 'Uptime hệ thống', value: '99.7%', color: '#10b981' },
    ],
    month: [
      { label: 'Mục tiêu phát hiện', value: 40240, color: '#1e6091', trend: { value: 6, positive: true } },
      { label: 'Cảnh báo đã xử lý', value: 1064, color: '#f59e0b' },
      { label: 'Cảnh báo nghiêm trọng', value: 12, color: '#ef4444', trend: { value: 20, positive: true } },
      { label: 'Uptime hệ thống', value: '99.6%', color: '#10b981' },
    ],
    quarter: [
      { label: 'Mục tiêu phát hiện', value: 121600, color: '#1e6091' },
      { label: 'Cảnh báo đã xử lý', value: 3192, color: '#f59e0b' },
      { label: 'Cảnh báo nghiêm trọng', value: 38, color: '#ef4444' },
      { label: 'Uptime hệ thống', value: '99.6%', color: '#10b981' },
    ],
    year: [
      { label: 'Mục tiêu phát hiện', value: 486400, color: '#1e6091' },
      { label: 'Cảnh báo đã xử lý', value: 12768, color: '#f59e0b' },
      { label: 'Cảnh báo nghiêm trọng', value: 156, color: '#ef4444' },
      { label: 'Uptime hệ thống', value: '99.5%', color: '#10b981' },
    ],
  },
  trendChart: {
    title: 'Mục tiêu phát hiện & cảnh báo (30 ngày)',
    categories: Array.from({ length: 30 }, (_, i) => `${i + 1}`),
    series: [
      { name: 'Mục tiêu (×100)', data: Array.from({ length: 30 }, () => Math.round(8 + Math.random() * 6)), color: '#1e6091' },
      { name: 'Cảnh báo', data: Array.from({ length: 30 }, () => Math.round(20 + Math.random() * 30)), color: '#f59e0b' },
    ],
  },
  distributionChart: {
    title: 'Cảnh báo theo loại',
    data: [
      { name: 'UAV/Flycam', value: 412, color: '#f59e0b' },
      { name: 'Chưa định danh', value: 328, color: '#06b6d4' },
      { name: 'Tốc độ vượt ngưỡng', value: 142, color: '#8b5cf6' },
      { name: 'Rời hành lang', value: 98, color: '#1e6091' },
      { name: 'Xâm nhập', value: 64, color: '#ef4444' },
      { name: 'Vùng cấm', value: 20, color: '#dc2626' },
    ],
  },
  templates: [
    { id: 'bd-1', code: 'BD-W-2026-W18', name: 'Báo cáo cảnh báo tuần 18', period: 'week', format: 'pdf', generatedAt: '2026-05-05T07:30:00', generatedBy: 'Thiếu tá Lê Quang Minh', size: '1.8 MB', status: 'ready' },
    { id: 'bd-2', code: 'BD-M-2026-04', name: 'Báo cáo cảnh báo tháng 4/2026', period: 'month', format: 'pdf', generatedAt: '2026-05-01T08:00:00', generatedBy: 'Thiếu tá Lê Quang Minh', size: '4.4 MB', status: 'ready' },
    { id: 'bd-3', code: 'BD-INC-M-2026-04', name: 'Báo cáo sự cố/cảnh báo nghiêm trọng tháng 4', period: 'month', format: 'docx', generatedAt: '2026-05-01T09:00:00', generatedBy: 'Đại tá Nguyễn Văn Hùng', size: '2.2 MB', status: 'ready' },
    { id: 'bd-4', code: 'BD-Q-2026-Q1', name: 'Báo cáo tổng hợp Quý 1', period: 'quarter', format: 'pdf', generatedAt: '2026-04-05T10:00:00', generatedBy: 'Đại tá Nguyễn Văn Hùng', size: '8.6 MB', status: 'ready' },
  ],
}

const HAU_CAN: ReportsModuleData = {
  moduleKey: 'hau-can',
  moduleTitle: 'Hậu cần — Vũ khí',
  modulePath: '/hau-can',
  color: '#dc2626',
  kpis: {
    week: [
      { label: 'Nhập kho', value: 24, suffix: 'phiếu', trend: { value: 8, positive: true } },
      { label: 'Xuất kho', value: 38, suffix: 'phiếu' },
      { label: 'Tiêu thụ NL', value: 124, suffix: 'KL', trend: { value: 12 } },
      { label: 'Cảnh báo mới', value: 4 },
    ],
    month: [
      { label: 'Nhập kho', value: 92, suffix: 'phiếu', trend: { value: 5, positive: true } },
      { label: 'Xuất kho', value: 156, suffix: 'phiếu' },
      { label: 'Tiêu thụ NL', value: 4820, suffix: 'KL' },
      { label: 'Sortie hỗ trợ', value: 240, suffix: 'lượt' },
    ],
    quarter: [
      { label: 'Nhập kho', value: 280, suffix: 'phiếu' },
      { label: 'Xuất kho', value: 462, suffix: 'phiếu' },
      { label: 'Định mức YTD', value: 76, suffix: '%', trend: { value: 4 } },
      { label: 'Sortie hỗ trợ', value: 720, suffix: 'lượt' },
    ],
    year: [
      { label: 'Nhập kho', value: 1180, suffix: 'phiếu' },
      { label: 'Xuất kho', value: 1920, suffix: 'phiếu' },
      { label: 'Định mức YTD', value: 76, suffix: '%' },
      { label: 'Sortie hỗ trợ', value: 2880, suffix: 'lượt' },
    ],
  },
  trendChart: {
    title: 'Tiêu thụ nhiên liệu 5 tháng',
    categories: ['T1', 'T2', 'T3', 'T4', 'T5'],
    series: [
      { name: 'Jet A-1 (KL)', data: [1820, 1740, 1680, 1340, 1260], color: '#06b6d4' },
      { name: 'JP-8 (KL)', data: [780, 720, 660, 540, 480], color: '#f59e0b' },
    ],
  },
  distributionChart: {
    title: 'Phân bổ giá trị tồn kho',
    data: [
      { name: 'Nhiên liệu', value: 62, color: '#06b6d4' },
      { name: 'Vũ khí', value: 18, color: '#ef4444' },
      { name: 'Phụ tùng', value: 14, color: '#f59e0b' },
      { name: 'Quân trang', value: 6, color: '#10b981' },
    ],
  },
  templates: [
    { id: 'hc-1', code: 'BC-HC-001', name: 'Báo cáo tổng hợp hậu cần tuần', period: 'week', format: 'pdf', generatedAt: '2026-05-05 16:00', generatedBy: 'Auto', size: '1.2 MB', status: 'ready' },
    { id: 'hc-2', code: 'BC-HC-002', name: 'Báo cáo tiêu hao vũ khí - đạn dược', period: 'month', format: 'xlsx', generatedAt: '2026-05-01 08:00', generatedBy: 'Auto', size: '480 KB', status: 'ready' },
    { id: 'hc-3', code: 'BC-HC-003', name: 'Báo cáo nhiên liệu hàng không', period: 'week', format: 'xlsx', generatedAt: '2026-05-06 06:00', generatedBy: 'Auto', size: '320 KB', status: 'ready' },
    { id: 'hc-4', code: 'BC-HC-004', name: 'Báo cáo MRO máy bay', period: 'quarter', format: 'docx', generatedAt: '2026-04-01 09:00', generatedBy: 'Th.tá Phạm', size: '2.1 MB', status: 'ready' },
    { id: 'hc-5', code: 'BC-HC-005', name: 'Báo cáo định mức tiêu hao YTD', period: 'month', format: 'pdf', generatedAt: '2026-05-01 10:00', generatedBy: 'Auto', size: '780 KB', status: 'ready' },
    { id: 'hc-6', code: 'BC-HC-006', name: 'Báo cáo sẵn sàng tác chiến', period: 'month', format: 'pdf', generatedAt: '—', generatedBy: '—', size: '—', status: 'scheduled' },
  ],
}

const DAO_TAO: ReportsModuleData = {
  moduleKey: 'dao-tao',
  moduleTitle: 'Đào tạo chuyên sâu',
  modulePath: '/dao-tao',
  color: '#a855f7',
  kpis: {
    week: [
      { label: 'Buổi học diễn ra', value: 184, color: '#1e6091' },
      { label: 'Pass rate tuần', value: '88.4%', color: '#10b981', trend: { value: 1.2, positive: true } },
      { label: 'Giờ mô phỏng', value: 412, suffix: 'h', color: '#06b6d4' },
      { label: 'CC cấp tuần', value: 6, color: '#a855f7' },
    ],
    month: [
      { label: 'Buổi học diễn ra', value: 824, color: '#1e6091' },
      { label: 'Pass rate tháng', value: '87.4%', color: '#10b981', trend: { value: 0.8, positive: true } },
      { label: 'Giờ mô phỏng', value: 1820, suffix: 'h', color: '#06b6d4' },
      { label: 'CC cấp tháng', value: 28, color: '#a855f7' },
    ],
    quarter: [
      { label: 'Buổi học diễn ra', value: 2480, color: '#1e6091' },
      { label: 'Pass rate quý', value: '86.5%', color: '#10b981' },
      { label: 'Giờ mô phỏng', value: 5420, suffix: 'h', color: '#06b6d4' },
      { label: 'CC cấp quý', value: 84, color: '#a855f7' },
    ],
    year: [
      { label: 'Buổi học diễn ra', value: 9620, color: '#1e6091' },
      { label: 'Pass rate năm', value: '85.8%', color: '#10b981' },
      { label: 'Giờ mô phỏng', value: 21680, suffix: 'h', color: '#06b6d4' },
      { label: 'CC cấp năm', value: TRAINING_KPIS.certificatesIssued, color: '#a855f7' },
    ],
  },
  trendChart: {
    title: 'Trend chất lượng đào tạo (5 tháng)',
    categories: ['T1', 'T2', 'T3', 'T4', 'T5'],
    series: [
      { name: 'Pass rate', data: [85, 86, 87, 88, 87.4], color: '#10b981' },
      { name: 'Tỉ lệ tham dự', data: [92, 93, 94, 95, 94], color: '#1e6091' },
      { name: 'Hài lòng GV', data: [89, 90, 91, 92, 92], color: '#a855f7' },
    ],
  },
  distributionChart: {
    title: 'Phân bố HV theo loại máy bay',
    data: ['Su-30MK2', 'Su-22M4', 'MiG-21', 'Yak-130', 'L-39', 'Mi-17', 'C-295'].map((t, i) => ({
      name: t,
      value: STUDENTS.filter((s) => {
        const cur = ['CUR-001', 'CUR-002', 'CUR-003', 'CUR-004', 'CUR-005', 'CUR-006', 'CUR-007'][i]
        return s.curriculumId === cur
      }).length,
      color: ['#1e6091', '#10b981', '#a855f7', '#f59e0b', '#06b6d4', '#ec4899', '#8b5cf6'][i],
    })),
  },
  templates: [
    { id: 'dt-1', code: 'BC-DT-Q', name: 'Báo cáo đào tạo theo quý', period: 'quarter', format: 'pdf', generatedAt: '2026-04-01T08:00:00', generatedBy: 'BTM Đào tạo', size: '4.2 MB', status: 'ready' },
    { id: 'dt-2', code: 'BC-DT-N', name: 'Báo cáo đào tạo năm', period: 'year', format: 'pdf', generatedAt: '2026-01-15T08:00:00', generatedBy: 'BTM Đào tạo', size: '12.4 MB', status: 'ready' },
    { id: 'dt-3', code: 'BC-PC-K', name: 'Báo cáo phân loại HV cuối khóa', period: 'quarter', format: 'xlsx', generatedAt: '2026-04-15T08:00:00', generatedBy: 'P.Đào tạo TĐ 923', size: '0.8 MB', status: 'ready' },
    { id: 'dt-4', code: 'BC-PR-T', name: 'Báo cáo pass rate theo tháng', period: 'month', format: 'pdf', generatedAt: '2026-05-01T08:00:00', generatedBy: 'BTM Đào tạo', size: '2.1 MB', status: 'ready' },
    { id: 'dt-5', code: 'BC-NS-Q', name: 'Báo cáo ngân sách HL quý', period: 'quarter', format: 'xlsx', generatedAt: '2026-04-05T08:00:00', generatedBy: 'P.Tài chính', size: '1.4 MB', status: 'ready' },
  ],
}

const USER_ADMIN: ReportsModuleData = {
  moduleKey: 'user-admin',
  moduleTitle: 'Quản trị Người dùng',
  modulePath: '/user-admin',
  color: '#a855f7',
  kpis: {
    week: [
      { label: 'User mới onboard', value: 16, color: '#10b981', trend: { value: 14, positive: true } },
      { label: 'Offboard', value: 5, color: '#ef4444' },
      { label: 'Access requests', value: 48, color: '#3b82f6' },
      { label: 'MFA enabled', value: '94%', color: '#10b981' },
    ],
    month: [
      { label: 'User mới onboard', value: 64, color: '#10b981', trend: { value: 8, positive: true } },
      { label: 'Offboard', value: 18, color: '#ef4444' },
      { label: 'Access requests', value: 220, color: '#3b82f6' },
      { label: 'MFA enabled', value: '94%', color: '#10b981' },
    ],
    quarter: [
      { label: 'User mới onboard', value: 184, color: '#10b981' },
      { label: 'Offboard', value: 56, color: '#ef4444' },
      { label: 'Access requests', value: 642, color: '#3b82f6' },
      { label: 'MFA enabled', value: '94%', color: '#10b981' },
    ],
    year: [
      { label: 'Tổng user', value: 2486, color: '#3b82f6' },
      { label: 'User mới năm', value: 320, color: '#10b981' },
      { label: 'Disabled', value: 42, color: '#ef4444' },
      { label: 'MFA enabled', value: '94%', color: '#10b981' },
    ],
  },
  trendChart: {
    title: 'Tăng trưởng user theo tháng',
    categories: ['T1', 'T2', 'T3', 'T4', 'T5'],
    series: [
      { name: 'Tổng user', data: [2402, 2418, 2440, 2462, 2486], color: '#3b82f6' },
    ],
  },
  distributionChart: {
    title: 'Phân bổ user theo loại',
    data: [
      { name: 'Phi công', value: 200, color: '#3b82f6' },
      { name: 'Sĩ quan', value: 1240, color: '#10b981' },
      { name: 'Kỹ thuật / Hậu cần', value: 480, color: '#f59e0b' },
      { name: 'Y tế', value: 56, color: '#ec4899' },
      { name: 'Admin', value: 42, color: '#7c3aed' },
      { name: 'Khác', value: 468, color: '#64748b' },
    ],
  },
  templates: [
    { id: 'ua-1', code: 'UA-W-2026-W18', name: 'Báo cáo người dùng tuần 18', period: 'week', format: 'pdf', generatedAt: '2026-05-05T08:00:00', generatedBy: 'Hệ thống tự động', size: '420 KB', status: 'ready' },
    { id: 'ua-2', code: 'UA-M-2026-04', name: 'Báo cáo phân loại user tháng 4/2026', period: 'month', format: 'xlsx', generatedAt: '2026-05-01T08:00:00', generatedBy: 'P.Quản trị', size: '1.2 MB', status: 'ready' },
    { id: 'ua-3', code: 'UA-MFA-Q-2026-Q1', name: 'Báo cáo MFA & bảo mật Quý 1', period: 'quarter', format: 'pdf', generatedAt: '2026-04-05T08:00:00', generatedBy: 'P.An ninh thông tin', size: '2.8 MB', status: 'ready' },
    { id: 'ua-4', code: 'UA-AR-M-2026-04', name: 'Báo cáo access requests tháng 4', period: 'month', format: 'xlsx', generatedAt: '2026-05-01T08:00:00', generatedBy: 'P.Quản trị', size: '680 KB', status: 'ready' },
  ],
}

export const REPORTS_BY_MODULE: Record<string, ReportsModuleData> = {
  'tac-chien': TAC_CHIEN,
  'ke-hoach-bay': KE_HOACH_BAY,
  'dan-duong': DAN_DUONG,
  'van-tai': VAN_TAI,
  'khi-tuong': KHI_TUONG,
  'bao-dong': BAO_DONG,
  'hau-can': HAU_CAN,
  'dao-tao': DAO_TAO,
  'user-admin': USER_ADMIN,
}

export const REPORTS_MODULES: ReportsModuleData[] = [
  TAC_CHIEN, KE_HOACH_BAY, DAN_DUONG, VAN_TAI, KHI_TUONG, BAO_DONG, HAU_CAN, DAO_TAO, USER_ADMIN,
]

export const ALL_REPORT_TEMPLATES: (ReportTemplate & { moduleKey: string; moduleTitle: string; moduleColor: string })[] =
  REPORTS_MODULES.flatMap((m) =>
    m.templates.map((t) => ({ ...t, moduleKey: m.moduleKey, moduleTitle: m.moduleTitle, moduleColor: m.color })),
  )
